import { motion } from 'framer-motion';
import { Sparkles, ArrowRight, Check } from 'lucide-react';
import { useState } from 'react';

// URL-encode helper for Netlify Forms submission
const encode = (data) =>
  Object.keys(data)
    .map((k) => encodeURIComponent(k) + '=' + encodeURIComponent(data[k]))
    .join('&');

export const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error
  const [errorMsg, setErrorMsg] = useState('');

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('submitting');
    setErrorMsg('');
    try {
      const res = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: encode({
          'form-name': 'contact',
          'bot-field': '',
          ...form,
        }),
      });
      if (!res.ok) throw new Error('Submission failed');
      setStatus('success');
      setForm({ name: '', email: '', message: '' });
    } catch (err) {
      setStatus('error');
      setErrorMsg('Something went wrong. Please try again in a moment.');
    }
  };

  return (
    <footer
      className="pt-20 lg:pt-32 pb-12 px-6 lg:px-12 border-t border-border-subtle"
      data-testid="footer"
    >
      <div className="max-w-5xl mx-auto">
        {/* Let's work together section */}
        <motion.section
          className="mb-20 lg:mb-28"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          data-testid="contact-section"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
            {/* Heading */}
            <div className="space-y-5">
              <p className="font-body text-xs text-accent-text uppercase tracking-wider">
                Get in touch
              </p>
              <h2 className="font-headline text-4xl lg:text-5xl xl:text-6xl text-foreground-primary tracking-tight leading-[1.05]">
                Let's work
                <br />
                together
                <Sparkles
                  className="inline-block w-6 h-6 lg:w-8 lg:h-8 text-accent ml-3 align-baseline"
                  strokeWidth={1.5}
                />
              </h2>
              <p className="font-body text-base lg:text-lg text-foreground-secondary leading-relaxed max-w-md">
                Have a project in mind, or just want to say hello?
                <br />
                Drop me a line.
              </p>
            </div>

            {/* Contact form */}
            <div>
              {status === 'success' ? (
                <motion.div
                  className="rounded-lg border border-accent/30 bg-accent/5 px-6 py-10 text-center"
                  initial={{ opacity: 0, scale: 0.97 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3 }}
                  data-testid="contact-success"
                >
                  <div className="w-12 h-12 rounded-full bg-accent/15 flex items-center justify-center mx-auto mb-4">
                    <Check className="w-6 h-6 text-accent" strokeWidth={2} />
                  </div>
                  <h3 className="font-heading text-xl text-foreground-primary mb-2">
                    Message sent
                  </h3>
                  <p className="font-body text-sm text-foreground-secondary">
                    Thanks for reaching out — I'll be in touch soon.
                  </p>
                </motion.div>
              ) : (
                <form
                  name="contact"
                  method="POST"
                  data-netlify="true"
                  data-netlify-honeypot="bot-field"
                  onSubmit={handleSubmit}
                  className="space-y-5"
                  data-testid="contact-form"
                >
                  {/* Hidden Netlify fields */}
                  <input type="hidden" name="form-name" value="contact" />
                  <p className="hidden">
                    <label>
                      Don't fill this out: <input name="bot-field" onChange={() => {}} />
                    </label>
                  </p>

                  <div>
                    <label
                      htmlFor="contact-name"
                      className="font-body text-xs text-foreground-secondary uppercase tracking-wider mb-2 block"
                    >
                      Name
                    </label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      value={form.name}
                      onChange={handleChange}
                      className="w-full bg-transparent border-b border-border-subtle py-2 font-body text-foreground-primary placeholder:text-foreground-secondary/50 focus:outline-none focus:border-accent transition-colors"
                      placeholder="Your name"
                      data-testid="contact-name-input"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="contact-email"
                      className="font-body text-xs text-foreground-secondary uppercase tracking-wider mb-2 block"
                    >
                      Email
                    </label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      value={form.email}
                      onChange={handleChange}
                      className="w-full bg-transparent border-b border-border-subtle py-2 font-body text-foreground-primary placeholder:text-foreground-secondary/50 focus:outline-none focus:border-accent transition-colors"
                      placeholder="you@example.com"
                      data-testid="contact-email-input"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="contact-message"
                      className="font-body text-xs text-foreground-secondary uppercase tracking-wider mb-2 block"
                    >
                      What can I help with?
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      rows={4}
                      value={form.message}
                      onChange={handleChange}
                      className="w-full bg-transparent border-b border-border-subtle py-2 font-body text-foreground-primary placeholder:text-foreground-secondary/50 focus:outline-none focus:border-accent transition-colors resize-none"
                      placeholder="Tell me a bit about your project..."
                      data-testid="contact-message-input"
                    />
                  </div>

                  {status === 'error' && (
                    <p
                      className="font-body text-sm text-red-600"
                      data-testid="contact-error"
                    >
                      {errorMsg}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={status === 'submitting'}
                    className="group inline-flex items-center gap-2 bg-foreground-primary text-background-primary px-6 py-3 rounded-full font-body text-sm hover:bg-foreground-primary/90 disabled:opacity-60 disabled:cursor-not-allowed transition-all"
                    data-testid="contact-submit-button"
                  >
                    {status === 'submitting' ? (
                      <>
                        <span>Sending...</span>
                        <span className="w-3 h-3 border-2 border-background-primary/40 border-t-background-primary rounded-full animate-spin" />
                      </>
                    ) : (
                      <>
                        <span>Send message</span>
                        <ArrowRight
                          className="w-4 h-4 group-hover:translate-x-0.5 transition-transform"
                          strokeWidth={1.75}
                        />
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </motion.section>

        {/* Bottom bar */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-8 border-t border-border-subtle">
          {/* Logo */}
          <div className="flex items-center gap-2">
            <span className="font-heading text-xl font-semibold text-foreground-primary">
              Jesse Wilson
            </span>
            <Sparkles className="w-4 h-4 text-accent" strokeWidth={1.5} />
          </div>

          {/* Social + copyright */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6">
            <a
              href="https://www.linkedin.com/in/jessewil/"
              target="_blank"
              rel="noopener noreferrer"
              className="font-body text-sm text-foreground-secondary hover:text-foreground-primary transition-colors duration-200"
              data-testid="footer-linkedin"
            >
              LinkedIn
            </a>
            <p className="font-body text-xs text-foreground-secondary">
              © {currentYear} All rights reserved
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
